import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isRSAA } from 'redux-api-middleware';

/**
 * @typedef {object} UseApiOptions
 * @property {boolean} fetchOnMount - if set to true, the fetch function will be invoked when the component mounts
 * @property {boolean} autoFetch - deprecated. instead, please use fetchOnDependenciesChange
 * @property {boolean} fetchOnDependenciesChange - if true, when dependencies change, the function will be invoked
 * @property {function} [fetchOnDependenciesChange] - optional callback when successfully returns data
 */

const DefaultOptions = {
  fetchOnMount: true,
  autoFetch: true,
  fetchOnDependenciesChange: true,
  onSuccessCallback: null,
  disabled: false,
};

/**
 * Call an api when a component is mounted. This hook will return a status object which
 * contains info about the async api fetch. Can be useful to determine loading states
 * and error states.
 *
 * @example FetchingAdjustmentDetails
 * function MyComponent({ adjustmentId }) {
 *   const { isLoading, error, data } = useAPI(() => fetchAdjustmentDetails(adjustmentId), [adjustmentId]);
 *   const adjustment = useMemo(() => get(data, `entities.adjustments.${adjustmentId}`, {}), [data]);
 *   const errorMessage = useMemo(() => {
 *    if (!error) {
 *      return null;
 *    }
 *
 *    if (error.status === 404) {
 *      return 'Not Found';
 *    }
 *
 *    return 'Something went wrong.'
 *   }, [error]);
 *
 *   if (isLoading) {
 *     return <Spinner />;
 *   }
 *
 *   if (error) {
 *     return <div>{errorMessage}</div>;
 *   }
 *
 *   return (
 *    <div>
 *      <h1>{adjustment.name}</h1>
 *    </div>
 *   )
 * }
 *
 * @param {Function} fn an action creator function that returns an API middleware event
 * @param {Any[]} dependencies an array of dependencies to run the function
 * @param {UseApiOptions} options options object. see DefaultOptions
 * @returns
 */
export default function useAPI(fn, dependencies = [], options = DefaultOptions) {
  const ref = useRef(() => {
    throw new Error('not initialized');
  });

  const dispatch = useDispatch();
  const selectedData =
    typeof options.selector === 'function' ? useSelector(options.selector) : options.selector;
  const [status, setStatus] = useState({
    isLoading: false,
    error: undefined,
    data: undefined,
    success: false,
  });

  async function fetchApi(...args) {
    const action = fn(...args);

    if (!isRSAA(action)) {
      throw new Error(
        `You must pass a function that returns a redux api middleware action. E.g. function fetchUsers() {  return { RSAA: { method: 'GET', endpoint: '/users' } }  }; const {isLoading} = useAPI(() => fetchUsers());}`,
      );
    }

    if (options.disabled) {
      return;
    }

    setStatus({ isLoading: true });

    const result = await dispatch(action);

    // dont set state on unmounted components
    if (!ref.current) {
      return;
    }

    // when the API bailout happens because of cache, we don't get
    // a result, so just ignore the null result
    if (!result) {
      setStatus({ isLoading: false });
      return;
    }

    if (result.error) {
      setStatus({ isLoading: false, error: result.payload });
      if (options.onErrorCallback) {
        options.onErrorCallback();
      }
    } else {
      setStatus({ isLoading: false, data: result.payload, success: true });

      if (options.onSuccessCallback) {
        options.onSuccessCallback();
      }
    }
  }

  useEffect(() => {
    ref.current = dispatch;

    if (options.fetchOnMount && !options.autoFetch) {
      fetchApi();
    }

    return () => {
      ref.current = null;
    };
  }, []);

  useEffect(() => {
    if (options.autoFetch || options.fetchOnDependenciesChange) {
      fetchApi();
    }
  }, dependencies);

  return {
    ...status,
    data: selectedData !== undefined ? selectedData : status.data,
    fetchApi,
  };
}
